// src/components/scratch/PaidAccessCard.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Icon from "../ui/Icon";

/**
 * Paywall card for the Scratch-Off Intelligence module.
 *
 * Renders when the user has no active scratch-off subscription (tier NONE),
 * or when an endpoint returns 402 with reason NO_SUBSCRIPTION.
 *
 * Three explicit tiers are summarized below the headline so the user can
 * choose with one click. Language strictly follows the policy: no purchase
 * recommendations, no winner-prediction claims.
 *
 * Props:
 *   tier        current tier name ("NONE" expected here)
 *   reason      optional reason from backend 402 ("TIER_TOO_LOW", etc.)
 *   featureName optional short label for the blocked feature
 */
export default function PaidAccessCard({ tier = "NONE", reason, featureName }) {
  const navigate = useNavigate();

  // Pick the headline based on what blocked the user.
  const headline = (() => {
    if (reason === "TIER_TOO_LOW") {
      return featureName
        ? `${featureName} needs a higher tier.`
        : "This feature needs a higher tier.";
    }
    if (reason === "NOT_ENOUGH_CREDITS") {
      return "You're out of credits for this report.";
    }
    if (reason === "STATE_NOT_ALLOWED") {
      return "Upgrade to PRO to switch states.";
    }
    return "Scratch-Off Intelligence is a paid feature.";
  })();

  return (
    <Card variant="panel" className="relative overflow-hidden">
      <DotPattern />

      <div className="relative z-10 text-center py-2">
        <div
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-3"
          style={{
            background: "rgba(255,255,255,0.08)",
            color: "var(--on-panel)",
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          <Icon name="lock" size={12} />
          Paid feature
        </div>

        <h2
          className="font-semibold leading-tight mb-2"
          style={{
            fontSize: "clamp(1.25rem, 3vw, 1.75rem)",
            color: "var(--on-panel)",
          }}
        >
          {headline}
        </h2>

        <p
          className="text-[13px] leading-relaxed mx-auto"
          style={{ color: "var(--on-panel-2)", maxWidth: 520 }}
        >
          We rank scratch-off games using public prize-inventory data published
          by state lotteries. Subscribe to see today's rankings, browse remaining
          prize strength by state, and optionally generate detailed reports.
          Scratch-off outcomes are random — this is for entertainment purposes
          only and does not improve or guarantee your odds.
        </p>

        {/* 3-tier mini-summary */}
        <div className="grid sm:grid-cols-3 gap-2 mt-6 mb-6 text-left">
          <TierSummary
            name="Plus"
            price="$5.99/mo"
            highlights={["30 credits/month", "One selected state", "Basic rankings"]}
          />
          <TierSummary
            name="Pro"
            price="$10.99/mo"
            featured
            highlights={[
              "60 credits/month",
              "Multi-state coverage",
              "Advanced score · alerts · trends",
            ]}
          />
          <TierSummary
            name="Elite"
            price="$19.99/mo"
            highlights={[
              "200 credits/month",
              "Full analytics",
              "Risk & Value Reports",
            ]}
          />
        </div>

        <div className="flex flex-wrap justify-center gap-3">
          <Button
            variant="panel"
            onClick={() => navigate("/plans")}
            icon="arrow-right"
            iconRight
          >
            Choose a plan
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/how-it-works")}
            style={{ background: "rgba(255,255,255,0.08)", color: "var(--on-panel)" }}
          >
            How it works
          </Button>
        </div>

        {tier && tier !== "NONE" && (
          <p
            className="mt-4 text-[11px]"
            style={{ color: "var(--on-panel-3)" }}
          >
            Current tier: <strong>{tier}</strong>
          </p>
        )}
      </div>
    </Card>
  );
}

/* ------------------------------ helpers ------------------------------ */

function TierSummary({ name, price, highlights, featured }) {
  return (
    <div
      className="rounded-xl p-4"
      style={{
        background: featured ? "rgba(46,229,107,0.12)" : "rgba(255,255,255,0.06)",
        border: `1px solid ${featured ? "var(--gold-border)" : "rgba(255,255,255,0.10)"}`,
      }}
    >
      <div className="flex items-baseline justify-between mb-2">
        <span
          className="font-semibold text-[14px]"
          style={{ color: featured ? "var(--gold)" : "var(--on-panel)" }}
        >
          {name}
        </span>
        <span
          className="text-[12px] font-medium"
          style={{ color: featured ? "var(--gold)" : "var(--on-panel-2)" }}
        >
          {price}
        </span>
      </div>
      <ul className="space-y-1">
        {highlights.map((h) => (
          <li
            key={h}
            className="flex items-start gap-1.5 text-[11.5px] leading-snug"
            style={{ color: "var(--on-panel-2)" }}
          >
            <Icon
              name="check"
              size={11}
              style={{
                color: featured ? "var(--gold)" : "var(--teal)",
                marginTop: 3,
                flexShrink: 0,
              }}
            />
            {h}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* Decorative dot pattern — same texture used on Landing.jsx panels */
function DotPattern() {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage:
          "radial-gradient(circle, rgba(255,255,255,0.10) 1px, transparent 1.4px)",
        backgroundSize: "18px 18px",
        maskImage: "radial-gradient(ellipse at center, black 50%, transparent 85%)",
        WebkitMaskImage:
          "radial-gradient(ellipse at center, black 50%, transparent 85%)",
      }}
    />
  );
}
